import { useState, useCallback } from "react";
import ScamReportAPI from "../../services/ScamReportAPI";

/**
 * Custom hook that encapsulates the inline share-with-family flow.
 *
 * Manages all share UI state (open/close, message, loading, success, error)
 * and provides a single `sendShare` function that shares an existing report.
 *
 * The scam report must already be saved (via ScamReportAPI.saveReport())
 * before sharing. The reportId is passed in `sendShare(reportId)`.
 *
 * @returns {{
 *   shareOpen: boolean,
 *   shareMessage: string,
 *   shareLoading: boolean,
 *   shareSuccess: boolean,
 *   shareError: string|null,
 *   setShareMessage: Function,
 *   toggleShare: Function,
 *   closeShare: Function,
 *   sendShare: Function,
 * }}
 */
const useShareAlert = () => {
  const [shareOpen, setShareOpen] = useState(false);
  const [shareMessage, setShareMessage] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [shareError, setShareError] = useState(null);

  const toggleShare = useCallback(() => {
    setShareOpen((prev) => !prev);
    setShareError(null);
    setShareSuccess(false);
    setShareMessage("");
  }, []);

  const closeShare = useCallback(() => {
    setShareOpen(false);
  }, []);

  /**
   * Share an existing scam report with the user's family.
   *
   * @param {number} reportId - ID of the saved scam report to share
   */
  const sendShare = useCallback(
    async (reportId) => {
      if (!reportId) {
        setShareError("No report available to share. Please try again.");
        return;
      }
      try {
        setShareLoading(true);
        setShareError(null);
        await ScamReportAPI.shareReport(reportId, shareMessage.trim() || null);
        setShareSuccess(true);
        setTimeout(() => setShareOpen(false), 2000);
      } catch (err) {
        setShareError(
          err?.response?.data?.errorMessage ||
            err?.message ||
            "Failed to share. Please try again.",
        );
      } finally {
        setShareLoading(false);
      }
    },
    [shareMessage],
  );

  return {
    shareOpen,
    shareMessage,
    shareLoading,
    shareSuccess,
    shareError,
    setShareMessage,
    toggleShare,
    closeShare,
    sendShare,
  };
};

export default useShareAlert;
